import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import {
    ISubscriptionSettingsResponse,
    ISubscriptionSettingsTopic,
    IVcfGeofenceCreatePayload,
    IVcfGeofenceResponse,
    IVcfGeofenceUpdatePayload,
    IVcfNotificationCreateOrUpdateResponse,
    IVcfNotificationDeleteResponse,
    IVcfSpeedAlertCreatePayload,
    IVcfSpeedAlertResponse,
    IVcfSpeedAlertUpdatePayload,
    IVcfUserCapabilitiesResponse,
    NotificationConfigRequestStatusResponse,
    TransactionHistoryResponse,
} from './types';

const headers = {
    'User-Id': '__userId__',
};

export class VcfNotificationClient {

    private readonly resourceHostId: string;

    constructor(private readonly authService: IAuthServiceV2, private readonly stage: string) {
        this.resourceHostId = `myvw-vcf-${stage}`;
    }

    private gdcTokenQueryString = (isWcar: boolean) => (isWcar ? `gdc=myvw-wcar-${this.stage}` : `gdc=myvw-mbb-${this.stage}`);

    public async getUserCapabilities(vin: string, isWcar: boolean): Promise<IVcfUserCapabilitiesResponse> {
        let response: Response;

        try {
            response = await this.authService.get(
                this.resourceHostId,
                `vehicles/${vin}/usercapabilities?${this.gdcTokenQueryString(isWcar)}`,
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(await response.json());
        }

        return json;
    }

    public async getTransactionHistory(vin: string, isWcar: boolean): Promise<TransactionHistoryResponse> {
        let response: Response;

        try {
            response = await this.authService.get(
                this.resourceHostId,
                `vehicles/${vin}/transactionhistory?${this.gdcTokenQueryString(isWcar)}`,
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        if (response.status === 404) {
            return {
                data: {
                    messages: [],
                    errors: [],
                },
            };
        }
        
        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }
        
        return json;
    }

    public async deleteTransactionHistory(
        vin: string,
        isWcar: boolean,
        category: 'speedalert' | 'geofence' | 'antitheftalarm',
    ): Promise<void> {
        let response: Response;

        try {
            response = await this.authService.delete(
                this.resourceHostId,
                `vehicles/${vin}/transactionhistory/${category}?${this.gdcTokenQueryString(isWcar)}`,
                undefined,
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        if (!response.ok) {
            throw new Error(await response.json());
        }
    }

    public async getGeofenceNotifications(vin: string, isWcar: boolean): Promise<IVcfGeofenceResponse> {
        let response: Response;

        try {
            response = await this.authService.get(
                this.resourceHostId,
                `vehicles/${vin}/alerts/geofence?${this.gdcTokenQueryString(isWcar)}`,
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        if (response.status === 404) {
            return {
                data: {
                    carCapturedTimestamp: '',
                    maxDefinitions: 0,
                    maxActiveDefinitions: 0,
                    geofences: [],
                },
            };
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async createGeofenceNotification(
        vin: string,
        isWcar: boolean,
        payload: IVcfGeofenceCreatePayload,
    ): Promise<IVcfNotificationCreateOrUpdateResponse> {
        let response: Response;

        try {
            response = await this.authService.post(
                this.resourceHostId,
                `vehicles/${vin}/alerts/geofence?${this.gdcTokenQueryString(isWcar)}`,
                JSON.stringify(payload),
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async updateGeofenceNotification(
        vin: string,
        isWcar: boolean,
        payload: IVcfGeofenceUpdatePayload,
    ): Promise<IVcfNotificationCreateOrUpdateResponse> {
        let response: Response;

        try {
            response = await this.authService.put(
                this.resourceHostId,
                `vehicles/${vin}/alerts/geofence?${this.gdcTokenQueryString(isWcar)}`,
                JSON.stringify(payload),
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async deleteGeofenceNotification(
        vin: string,
        isWcar: boolean,
        id: string,
    ): Promise<IVcfNotificationDeleteResponse> {
        let response: Response;

        try {
            response = await this.authService.delete(
                this.resourceHostId,
                `vehicles/${vin}/alerts/geofence/${id}?${this.gdcTokenQueryString(isWcar)}`,
                undefined,
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async getSpeedAlertNotifications(vin: string, isWcar: boolean): Promise<IVcfSpeedAlertResponse> {
        let response: Response;

        try {
            response = await this.authService.get(
                this.resourceHostId,
                `vehicles/${vin}/alerts/speed?${this.gdcTokenQueryString(isWcar)}`,
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        if (response.status === 404) {
            return {
                data: {
                    carCapturedTimestamp: '',
                    maxDefinitions: 0,
                    maxActiveDefinitions: 0,
                    speedAlerts: [],
                },
            };
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async createSpeedAlertNotification(
        vin: string,
        isWcar: boolean,
        payload: IVcfSpeedAlertCreatePayload,
    ): Promise<IVcfNotificationCreateOrUpdateResponse> {
        let response: Response;

        try {
            response = await this.authService.post(
                this.resourceHostId,
                `vehicles/${vin}/alerts/speed?${this.gdcTokenQueryString(isWcar)}`,
                JSON.stringify(payload),
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async updateSpeedAlertNotification(
        vin: string,
        isWcar: boolean,
        payload: IVcfSpeedAlertUpdatePayload,
    ): Promise<IVcfNotificationCreateOrUpdateResponse> {
        let response: Response;

        try {
            response = await this.authService.put(
                this.resourceHostId,
                `vehicles/${vin}/alerts/speed?${this.gdcTokenQueryString(isWcar)}`,
                JSON.stringify(payload),
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async deleteSpeedAlertNotification(
        vin: string,
        isWcar: boolean,
        id: string,
    ): Promise<IVcfNotificationDeleteResponse> {
        let response: Response;

        try {
            response = await this.authService.delete(
                this.resourceHostId,
                `vehicles/${vin}/alerts/speed/${id}/?${this.gdcTokenQueryString(isWcar)}`,
                undefined,
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async getSubscriptionSettings(vin: string, isWcar: boolean): Promise<ISubscriptionSettingsResponse> {
        let response: Response;

        try {
            response = await this.authService.get(
                this.resourceHostId,
                `vehicles/${vin}/subscriptions/settings?${this.gdcTokenQueryString(isWcar)}`,
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        const json = await response.json();

        if (!response.ok) {
            throw new Error(json);
        }

        return json;
    }

    public async updateSubscriptionSettings(
        vin: string,
        isWcar: boolean,
        payload: { topics: ISubscriptionSettingsTopic[] },
    ): Promise<void> {
        let response: Response;

        try {
            response = await this.authService.put(
                this.resourceHostId,
                `vehicles/${vin}/subscriptions/settings?${this.gdcTokenQueryString(isWcar)}`,
                JSON.stringify(payload),
                headers,
            );
        } catch (error: any) {
            throw new Error(error);
        }

        if (!response.ok) {
            throw new Error(await response.json());
        }
    }

    public async getNotificationConfigRequestStatus(
        vin: string,
        isWcar: boolean,
        category: 'speed' | 'geofence',
        requestId: string,
    ): Promise<NotificationConfigRequestStatusResponse> {
        const response = await this.authService.get(
            this.resourceHostId,
            `vehicles/${vin}/alerts/${category}/requests/${requestId}?${this.gdcTokenQueryString(isWcar)}`,
            headers,
        );

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return response.json();
    }
}
